import { parse } from 'date-fns';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { OrderManagementResource } from '../../resources';
import { OrderLog, orderLogsAtom } from '../../state';
import { OrderHistoryItem, PeriodType } from '../../types';
import { isDefined } from '../../utils';
import { GetResourceHeaderForMultiAccount } from '../useResource';

const EXPIRY_DATE_FORMAT = 'yyyy-MM-dd';

export type UseOrderLogsCommonProps = {
  orderManagementResource: OrderManagementResource;
  getResourceHeaderForMultiAccount: GetResourceHeaderForMultiAccount;
};

export const useOrderLogsCommon = ({
  orderManagementResource,
  getResourceHeaderForMultiAccount,
}: UseOrderLogsCommonProps) => {
  const [orderLogs, setOrderLogs] = useAtom(orderLogsAtom);

  const mapOrderHistoryItemToOrderLog = useCallback((orderHistoryItem: OrderHistoryItem) => {
    const orderLog: OrderLog = {
      orderId: orderHistoryItem.id,
      accountNumber: orderHistoryItem.accountNumber,
      transactionDate: new Date(orderHistoryItem.transactionDate),
      logEntryType: orderHistoryItem.logEntryType,
      ticker: orderHistoryItem.ticker,
      tradeType: orderHistoryItem.direction,
      details: {
        instrumentName: orderHistoryItem.instrumentName,
        price: orderHistoryItem.price,
        ccy: orderHistoryItem.ccy,
        orderType: orderHistoryItem.orderType,
        quantity: orderHistoryItem.quantity,
        stopActivationPrice: orderHistoryItem.stopActivationPrice,
        tradeType: orderHistoryItem.direction,
        expiryType: orderHistoryItem.expiryType,
        expiry: getExpiryDate(orderHistoryItem.expiry),
        rejectReason: orderHistoryItem.rejectReason,
        orderValue: orderHistoryItem.orderValue,
        commission: orderHistoryItem.commission,
        tradingPhase: orderHistoryItem.tradingPhase,
        instrumentCategory: orderHistoryItem.instrumentCategory,
      },
    };

    return orderLog;
  }, []);

  const loadOrderLogs = useCallback(
    async (periodType: PeriodType): Promise<void> => {
      setOrderLogs({ orderLogs: [], isLoading: true, error: false, periodType });
      try {
        const response = await orderManagementResource.getOrderHistory(
          { periodType },
          getResourceHeaderForMultiAccount(),
        );
        setOrderLogs({
          orderLogs: response.data.map(_ => mapOrderHistoryItemToOrderLog(_)),
          isLoading: false,
          error: false,
          periodType,
        });
      } catch (error) {
        setOrderLogs({ orderLogs: [], isLoading: false, error: true, periodType });
      }
    },
    [setOrderLogs, mapOrderHistoryItemToOrderLog, orderManagementResource, getResourceHeaderForMultiAccount],
  );

  return { orderLogs, loadOrderLogs };
};

function getExpiryDate(expiry: string | undefined | null): Date | undefined {
  if (!isDefined(expiry)) {
    return undefined;
  }
  try {
    return parse(expiry, EXPIRY_DATE_FORMAT, new Date());
  } catch (e) {
    return undefined;
  }
}
